import { InjectionToken } from '@angular/core';
import { BaseDocument } from '../types';
import { DesignDocument } from './design-document.interface';

/**
 * Handler-Interface für dokumentspezifische Operationen.
 * Ermöglicht app-spezifische Logik für Transformation, Validierung,
 * Enrichment und Filterung von Dokumenten.
 *
 * @template T Document-Typ, muss von BaseDocument erben
 */
export interface DocumentHandler<T extends BaseDocument> {
	/**
	 * Liefert Design-Dokumente (CouchDB Views und Filter) für diesen Document-Typ.
	 * Wird beim Initialisieren der Datenbank verwendet.
	 */
	getDesignDocuments(): DesignDocument[];

	/**
	 * Transformiert ein Roh-Dokument nach dem Laden aus der Datenbank.
	 * Wird für Migration, Updates und Normalisierung verwendet.
	 *
	 * @param raw Roh-Dokument aus der Datenbank
	 * @returns Transformiertes Dokument
	 */
	transformAfterLoad(raw: any): T;

	/**
	 * Enrichment: Reichert ein Dokument mit zusätzlichen Daten an.
	 * Z.B. WMS-Abfragen für Geodaten, externe API-Calls, etc.
	 * Modifiziert das Dokument in-place.
	 *
	 * @param doc Dokument zum Anreichern
	 */
	enrichDocument(doc: T): void;

	/**
	 * Prüft ob ein Dokument in einer Region liegt (räumliche Filterung).
	 * Wird beim Checkout/Download verwendet um nur relevante Dokumente zu laden.
	 *
	 * @param doc Zu prüfendes Dokument
	 * @param region Region zum Filtern (Format app-spezifisch)
	 * @returns true wenn Dokument in Region liegt
	 */
	isDocumentInRegion(doc: T, region: any): boolean;

	/**
	 * Transformiert ein Dokument vor dem Speichern in die Datenbank.
	 * Z.B. für Normalisierung, Entfernen transienter Felder, etc.
	 *
	 * @param doc Zu transformierendes Dokument
	 * @returns Transformiertes Dokument
	 */
	transformForSave(doc: T): T;

	/**
	 * Liefert hardcodierte Lookup-Daten (z.B. Bodenarten, Farben, etc.).
	 * Wird von getHardcoded() gecacht.
	 *
	 * @returns Map mit Tabellennamen als Keys und Arrays als Values
	 */
	getHardData(): Map<string, any[]>;

	/**
	 * Factory-Methode: Erstellt ein neues Dokument mit Default-Werten.
	 * Ersetzt app-spezifische create-Methoden (z.B. createDocumentFrom).
	 *
	 * @param partial Optional: Partielle Daten zum Initialisieren
	 * @returns Neues Dokument
	 */
	createNew(partial?: Partial<T>): T;
}

/**
 * InjectionToken für DocumentHandler.
 * Erlaubt app-spezifische Implementierung per DI.
 */
export const DOCUMENT_HANDLER = new InjectionToken<DocumentHandler<any>>('DocumentHandler');
